import { useEffect, useRef, useState } from "react";
import { Users, Crosshair, Heart, RotateCcw, ChevronRight, Coins, Fuel, Shield } from "lucide-react";
import { RoadEngine } from "./roadEngine";
import { SpaceHomeBtn } from "./SpaceNav";
import { sfx } from "./audio";
import { spaceApi } from "./spaceApi";

const R = "/game/road";
const BASE_BGM = "/assets/vault/audio/command_deck_theme.mp3";
const HERO_SPRITES = { jess: `${R}/hero_jess.png`, nancy: `${R}/hero_nancy.png`, blaze: `${R}/hero_blaze.png` };
const ENEMY_SPRITES = ["/game/surface/enemy_sprawler.png", "/game/surface/enemy_acid_hatchling.png"];
const SWEEP_BG = { 1: `${R}/bg_stage1.jpg` };

// Phase B surface-clearing mission: deploy the squad into a wide-lane desert
// sweep vs a Zornelex swarm. Clearing the run lifts the sector's fog of war.
// (The Combat Vehicle from Option A will layer onto this same engine mount.)
export default function SurfaceSweep({ sector, reward, vehicle, onWin, onBack }) {
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const [phase, setPhase] = useState("playing"); // playing | victory | gameover
  const [hud, setHud] = useState({ troops: 0, weaponTier: 2, lives: 3, stage: 1, progress: 0, armor: 0, armorMax: 0 });
  const [runId, setRunId] = useState(0);

  useEffect(() => {
    setPhase("playing");
    setHud({ troops: 0, weaponTier: 2, lives: 3, stage: 1, progress: 0, armor: vehicle?.armor || 0, armorMax: vehicle?.armor || 0 });
    const eng = new RoadEngine(canvasRef.current, {
      campaign: true,
      surfaceSweep: true,
      vehicle: vehicle || null,
      alienSprites: ENEMY_SPRITES,
      initialRoster: ["jess", "nancy", "blaze"], // Jess · Nancy · Blaze
      enableRescue: false,
      startWeapon: 2,
      troopShield: true,
      healPulse: true,
      stageLen: 600 + (sector?.difficulty || 12) * 22,
      heroSprites: HERO_SPRITES, stageBackdrops: SWEEP_BG,
      onUpdate: (s) => setHud(s),
      onFinish: () => { sfx.victory(); setPhase("victory"); },
      onGameOver: () => { sfx.defeat(); setPhase("gameover"); },
    });
    engineRef.current = eng;
    eng.mount();
    spaceApi.gameAssets().then((a) => sfx.startBgm(a.road_patrol_theme || a.combat_theme || a.mist_theme)).catch(() => {});
    const onResize = () => eng.resize();
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); eng.unmount(); sfx.startBgm(BASE_BGM); };
  }, [runId, sector]);

  const retry = () => setRunId((n) => n + 1);

  return (
    <div className="ss-viewport rp-viewport" data-testid="surface-sweep">
      <SpaceHomeBtn onClick={onBack} testid="surface-sweep-back" />

      <canvas ref={canvasRef} className="ss-canvas" data-testid="surface-sweep-canvas" />
      <div className="surface-sandstorm" data-testid="surface-sandstorm" />

      <div className="rp-hud" data-testid="surface-sweep-hud">
        <div className="rp-stat"><b>SWEEP</b><span>{sector?.name}</span></div>
        <div className="rp-stat"><Users size={14} /> <b>{hud.troops}</b><span>buffer</span></div>
        <div className="rp-stat wpn"><Crosshair size={14} /> T<b>{hud.weaponTier}</b></div>
        {hud.armorMax > 0 && (
          <div data-testid="surface-sweep-armor" style={{ minWidth: 92, display: "flex", alignItems: "center", gap: 5 }}>
            <Shield size={14} color={hud.armor / hud.armorMax > 0.3 ? "#37e0a0" : "#ff8a5c"} />
            <b style={{ fontSize: 11, color: "#e6eefb" }}>{hud.armor}/{hud.armorMax}</b>
            <div style={{ height: 6, width: 34, borderRadius: 3, background: "rgba(255,255,255,0.14)", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${Math.max(0, (hud.armor / hud.armorMax) * 100)}%`, background: hud.armor / hud.armorMax > 0.3 ? "#37e0a0" : "#ff8a5c", transition: "width .2s" }} />
            </div>
          </div>
        )}
        <div className="rp-lives" data-testid="surface-sweep-lives">
          {[0, 1, 2].map((i) => <Heart key={i} size={15} fill={i < hud.lives ? "#ff4560" : "none"} stroke={i < hud.lives ? "#ff4560" : "#5b6b82"} />)}
        </div>
        <div className="rp-progress"><span style={{ width: `${hud.progress * 100}%` }} /></div>
      </div>

      {phase === "playing" && (
        <div className="rp-hint" data-testid="surface-sweep-hint">Drag to steer · gun down the swarm · reach the end to secure the sector</div>
      )}

      {phase === "victory" && (
        <div className="ss-overlay" data-testid="surface-sweep-victory" style={{ background: "rgba(6,8,14,0.9)" }}>
          <span className="ov-t" style={{ color: "#ffd18a" }}>SECTOR SECURED</span>
          <span className="ov-s">Fog of war lifted over <b>{sector?.name}</b>. Salvage recovered:</span>
          <div style={{ display: "flex", gap: 14, margin: "12px 0" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 6, color: "#e5e4e2", fontWeight: 900 }}><Coins size={18} /> +{reward?.titanium_alloy} Titanium</span>
            <span style={{ display: "flex", alignItems: "center", gap: 6, color: "#6ee06e", fontWeight: 900 }}><Fuel size={18} /> +{reward?.bio_fuel} Bio-Fuel</span>
          </div>
          <button className="ss-enter-btn" data-testid="surface-sweep-claim" onClick={onWin}
            style={{ borderColor: "rgba(255,152,0,0.7)", background: "rgba(255,152,0,0.16)", color: "#ffd8a8" }}>
            Claim &amp; Return <ChevronRight size={16} />
          </button>
        </div>
      )}

      {phase === "gameover" && (
        <div className="ss-overlay lose" data-testid="surface-sweep-gameover">
          <span className="ov-t">Squad Overrun</span>
          <span className="ov-s">The swarm pushed you back. The sector holds its fog. Regroup and sweep again.</span>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="ss-enter-btn" data-testid="surface-sweep-retry" onClick={retry}><RotateCcw size={16} /> Retry Sweep</button>
            <button className="ss-enter-btn" data-testid="surface-sweep-abort" onClick={onBack} style={{ opacity: 0.85 }}>Return to Map</button>
          </div>
        </div>
      )}
    </div>
  );
}
